/** Photometric pipeline: linearisation, primaries, measured quantity, dark/flat correction. */
import { PRIMARIES, TRANSFER_LABELS } from '../../core/color';
import { QUANTITIES, type ClipDetection, type DarkMode, type QuantityId } from '../../core/pipeline';
import type { PrimariesId, TransferId } from '../../core/types';
import { fmt } from '../../core/units';
import { ctxA, frames, measurement, pipeline, quantity, updateSignal } from '../../app/store';
import { Field, Hint, NumberField, NumberInput, Section, Segmented, SelectField, Toggle } from '../controls';

export function PhotometryPanel() {
  const p = pipeline.value;
  const ctx = ctxA.value;
  const enc = ctx?.enc;
  const hint = frames.A.value?.meta.encoding;
  const m = measurement.value;
  const setTP = (patch: Partial<typeof p.transferParams>) => updateSignal(pipeline, { transferParams: { ...p.transferParams, ...patch } });
  const effTransfer = enc?.transfer ?? p.transfer;
  const transferOptions = (Object.keys(TRANSFER_LABELS) as TransferId[]).map((t) => ({
    value: t,
    label: TRANSFER_LABELS[t],
    disabled: t === 'curve' && !hint?.curve,
  }));
  return (
    <div class="photometry-panel">
      <SelectField<QuantityId>
        label="Measured quantity"
        help="photometry.quantity"
        testId="quantity"
        value={quantity.value}
        options={QUANTITIES.map((q) => ({ value: q.id, label: q.label }))}
        onChange={(v) => (quantity.value = v)}
      />
      {m && (
        <div class="resolved" data-help="photometry.resolved">
          Unit: <b>{m.unit || '—'}</b>
          {m.error && <span class="warn-text"> · {m.error}</span>}
        </div>
      )}
      <Section title="Linearisation (EOTF)" help="photometry.eotf">
        <Field label="Mode" help="photometry.mode">
          <Segmented
            value={p.transferMode}
            testId="transfer-mode"
            options={[
              { value: 'auto', label: 'Automatic' },
              { value: 'manual', label: 'Manual' },
            ]}
            onChange={(v) => updateSignal(pipeline, { transferMode: v })}
          />
        </Field>
        {p.transferMode === 'auto' && hint && (
          <Hint>
            Detected: <b>{TRANSFER_LABELS[hint.transfer]}</b>
            {hint.transfer === 'gamma' && hint.gamma ? ` (γ ${fmt(hint.gamma, 4)})` : ''} — {hint.description}
          </Hint>
        )}
        {p.transferMode === 'manual' && (
          <SelectField<TransferId>
            label="Transfer function"
            help="photometry.transfer"
            testId="transfer"
            value={p.transfer}
            options={transferOptions}
            onChange={(v) => updateSignal(pipeline, { transfer: v })}
          />
        )}
        {effTransfer === 'gamma' && p.transferMode === 'manual' && (
          <NumberField
            label="Gamma γ"
            help="photometry.gamma"
            value={p.transferParams.gamma}
            min={0.1}
            max={10}
            step={0.05}
            onChange={(v) => setTP({ gamma: v })}
          />
        )}
        {effTransfer === 'bt1886' && (
          <NumberField
            label="Black level Lb / Lw"
            help="photometry.bt1886"
            value={p.transferParams.bt1886Black}
            min={0}
            max={0.2}
            step={0.0005}
            onChange={(v) => setTP({ bt1886Black: v })}
          />
        )}
        {effTransfer === 'hlg' && (
          <>
            <Toggle
              label="Apply HLG OOTF (display light)"
              checked={p.transferParams.hlgOotf}
              help="photometry.hlgOotf"
              onChange={(v) => setTP({ hlgOotf: v })}
            />
            <NumberField
              label="Nominal peak"
              help="photometry.hlgPeak"
              value={p.transferParams.hlgPeak}
              unit="cd/m²"
              min={100}
              max={10000}
              step={100}
              onChange={(v) => setTP({ hlgPeak: v })}
            />
          </>
        )}
      </Section>
      <Section title="Colour primaries" help="photometry.primaries">
        <Field label="Mode" help="photometry.mode">
          <Segmented
            value={p.primariesMode}
            options={[
              { value: 'auto', label: 'Automatic' },
              { value: 'manual', label: 'Manual' },
            ]}
            onChange={(v) => updateSignal(pipeline, { primariesMode: v })}
          />
        </Field>
        {p.primariesMode === 'auto' && hint && (
          <Hint>
            Detected: <b>{hint.primaries === 'custom' ? 'custom (from file)' : PRIMARIES[hint.primaries].label}</b>
          </Hint>
        )}
        {p.primariesMode === 'manual' && (
          <SelectField<PrimariesId>
            label="Primaries"
            help="photometry.primariesSelect"
            value={p.primaries}
            options={[
              ...Object.entries(PRIMARIES).map(([k, v]) => ({ value: k as PrimariesId, label: v.label })),
              { value: 'custom', label: 'Custom chromaticities…' },
            ]}
            onChange={(v) => updateSignal(pipeline, { primaries: v })}
          />
        )}
        {p.primariesMode === 'manual' && p.primaries === 'custom' && <CustomPrimaries />}
        {enc && (
          <div class="luma-weights" data-help="photometry.weights">
            Y = <span class="mono">{fmt(enc.rgbToXyz[3], 4)}</span>·R + <span class="mono">{fmt(enc.rgbToXyz[4], 4)}</span>·G +{' '}
            <span class="mono">{fmt(enc.rgbToXyz[5], 4)}</span>·B
          </div>
        )}
      </Section>
      <Section title="Dark & flat correction" defaultOpen={p.darkMode !== 'none' || p.flatEnabled} help="photometry.darkflat">
        <SelectField<DarkMode>
          label="Dark correction"
          help="photometry.dark"
          value={p.darkMode}
          options={[
            { value: 'none', label: 'None' },
            { value: 'constant', label: 'Constant offset' },
            { value: 'frame', label: 'Dark frame (slot)' },
          ]}
          onChange={(v) => updateSignal(pipeline, { darkMode: v })}
        />
        {p.darkMode === 'constant' && (
          <NumberField
            label="Offset (linear)"
            help="photometry.darkConstant"
            value={p.darkConstant}
            step={0.001}
            onChange={(v) => updateSignal(pipeline, { darkConstant: v })}
          />
        )}
        <Toggle
          label="Flat-field correction (flat slot)"
          checked={p.flatEnabled}
          help="photometry.flat"
          onChange={(v) => updateSignal(pipeline, { flatEnabled: v })}
        />
      </Section>
      <Section title="Advanced" defaultOpen={p.clipDetection !== 'auto'} help="photometry.advanced">
        <NumberField
          label="L* reference white Yn"
          help="photometry.lstarWhite"
          value={p.lstarWhite}
          min={1e-9}
          step={0.01}
          onChange={(v) => updateSignal(pipeline, { lstarWhite: v })}
        />
        <SelectField<ClipDetection>
          label="Saturation flags"
          help="photometry.clipDetection"
          testId="clip-detection"
          value={p.clipDetection}
          options={[
            { value: 'auto', label: 'Automatic (off for screen content)' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
          onChange={(v) => updateSignal(pipeline, { clipDetection: v })}
        />
        <NumberField
          label="Float saturation level"
          help="photometry.floatClip"
          value={p.floatClipLevel}
          allowInfinity
          min={0}
          step={0.1}
          onChange={(v) => updateSignal(pipeline, { floatClipLevel: v })}
        />
      </Section>
      {ctx &&
        ctx.errors.length > 0 &&
        ctx.errors.map((e) => (
          <Hint kind="error" key={e}>
            {e}
          </Hint>
        ))}
    </div>
  );
}

function CustomPrimaries() {
  const p = pipeline.value;
  const c = p.customPrimaries;
  const set = (k: 'r' | 'g' | 'b' | 'w', i: 0 | 1, v: number) => {
    const next = structuredClone(c);
    next[k][i] = v;
    updateSignal(pipeline, { customPrimaries: next });
  };
  return (
    <table class="xy-table" data-help="photometry.custom">
      <thead>
        <tr>
          <th />
          <th>x</th>
          <th>y</th>
        </tr>
      </thead>
      <tbody>
        {(['r', 'g', 'b', 'w'] as const).map((k) => (
          <tr key={k}>
            <th>{{ r: 'Red', g: 'Green', b: 'Blue', w: 'White' }[k]}</th>
            <td>
              <NumberInput value={c[k][0]} min={0} max={1} step={0.001} onChange={(v) => set(k, 0, v)} />
            </td>
            <td>
              <NumberInput value={c[k][1]} min={0.0001} max={1} step={0.001} onChange={(v) => set(k, 1, v)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
